import { campaignPage } from "@/lib/campaign-pages";

export type Attribution = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  landing_page?: string;
  referrer?: string;
  ref_code?: string;
  first_touch_at?: string;
};

const STORAGE_KEY = "als_attribution";

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

function truncate(value: string, max: number) {
  return value.length > max ? value.slice(0, max) : value;
}

/**
 * Capture first-touch marketing attribution (UTMs + gclid) into sessionStorage.
 * First touch wins: later page views in the same session never overwrite it.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  try {
    if (window.sessionStorage.getItem(STORAGE_KEY)) return;

    const params = new URLSearchParams(window.location.search);
    const attribution: Attribution = {};

    for (const key of UTM_KEYS) {
      const value = params.get(key);
      if (value) attribution[key] = truncate(value, 255);
    }

    const click =
      params.get("gclid") || params.get("gbraid") || params.get("wbraid");
    if (click) attribution.gclid = truncate(click, 255);

    const ref = params.get("ref") || params.get("ref_code");
    if (ref) attribution.ref_code = truncate(ref, 80);
    attribution.first_touch_at = new Date().toISOString();

    attribution.landing_page = truncate(window.location.href, 500);

    const referrer = typeof document !== "undefined" ? document.referrer : "";
    if (referrer) attribution.referrer = truncate(referrer, 500);

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // private browsing / blocked storage, fail silently
  }
}

export function getAttribution(): Attribution {
  if (typeof window === "undefined") return {};

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    return {};
  }
}

/** True when this session arrived from a tagged campaign link (utm_* or ref). */
export function isCampaignVisit(): boolean {
  const a = getAttribution();
  return Boolean(a.utm_source || a.utm_medium || a.utm_campaign || a.utm_content || a.ref_code);
}

/** URL ref wins; otherwise the current page's default campaign ref. */
export function resolveRefCode(fallback?: string): string | undefined {
  if (typeof window === "undefined") return fallback;
  return getAttribution().ref_code ?? fallback ?? campaignPage(window.location.pathname)?.ref;
}

/** Campaign fields attached to every GA4 event. */
export function campaignParams() {
  const a = getAttribution();
  return {
    utm_source: a.utm_source,
    utm_medium: a.utm_medium,
    utm_campaign: a.utm_campaign,
    utm_content: a.utm_content,
    ref_code: resolveRefCode(),
  };
}
